import React from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { computeJurorScores } from '../../db/selectors/scoreComputation';
import { getScoreColor } from '../../util/scoreColors';
import ScoreDetailPopover from './ScoreDetailPopover';

/**
 * Renders a compact pill per score with `showOnJurorCard !== false`,
 * computed live from `scoreDefinitions`/`scoringRules` + the juror's
 * `demographics`. Each score gets a fixed outline color (see
 * util/scoreColors) instead of spelling out its name, so a card can carry
 * several scores without a wall of text — hovering shows the full name +
 * completeness (same "value visible, name on hover" pattern as the
 * demographics block), and clicking still opens the full contribution
 * breakdown (ScoreDetailPopover). Meant to sit inline alongside demographics
 * rather than in its own block. Renders nothing when there are no visible
 * scores — callers don't need to guard for that themselves.
 */
const ScoreBadges = ({ scoreDefinitions, scoringRules, juror, size = 'small' }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedScore, setSelectedScore] = React.useState(null);

  const scores = React.useMemo(
    () => computeJurorScores({ scoreDefinitions, scoringRules }, juror),
    [scoreDefinitions, scoringRules, juror],
  );
  const visibleScores = scores.filter((score) => score.showOnJurorCard);

  if (visibleScores.length === 0) {
    return null;
  }

  const handleClick = (event, score) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedScore(score);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedScore(null);
  };

  return (
    <React.Fragment>
      <Stack direction="row" spacing={0.5} component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
        {visibleScores.map((score) => {
          const color = getScoreColor(score.scoreDefinitionId, scoreDefinitions);
          return (
            <Tooltip
              key={score.scoreDefinitionId}
              title={`${score.name}: ${score.total > 0 ? '+' : ''}${score.total} (${score.answeredCount}/${score.ruleCategoryCount} answered)`}
            >
              <Chip
                label={score.total}
                size={size}
                variant="outlined"
                onClick={(event) => handleClick(event, score)}
                sx={{
                  height: 18,
                  fontSize: 11,
                  fontWeight: 'bold',
                  color,
                  borderColor: color,
                  '& .MuiChip-label': {
                    px: 0.75,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Stack>
      <ScoreDetailPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        score={selectedScore}
      />
    </React.Fragment>
  );
};

export default ScoreBadges;
